import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Card,
  Row,
  Col,
  Tag,
  Space,
  Empty,
  Skeleton,
  Badge,
  Popconfirm,
  Select,
  theme,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { departmentService } from '../../services/departmentService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import DepartmentForm from '../../components/department/DepartmentForm';
import DepartmentMembersModal from '../../components/department/DepartmentMembersModal';
import notify from '../../utils/notify';
import getApiErrorMessage from '../../utils/getApiErrorMessage';

const { Title, Paragraph } = Typography;
const { useToken } = theme;

const DepartmentList = () => {
  const { canManageDepartments, canAccessEmployees } = useAuth();
  const { token } = useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  const handleDepartmentCardClick = (department) => {
    if (canAccessEmployees()) {
      navigate(`/employees?department=${department.id}`);
    } else {
      setSelectedDepartment(department);
      setMembersModalOpen(true);
    }
  };

  useEffect(() => {
    fetchDepartments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getAll();
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      notify.error(t('department.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = () => {
    setEditingDepartment(null);
    setFormOpen(true);
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setFormOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingDepartment) {
        await departmentService.update(editingDepartment.id, formData);
        notify.success(t('department.updateSuccess'));
      } else {
        await departmentService.create(formData);
        notify.success(t('department.createSuccess'));
      }
      setFormOpen(false);
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department:', error);
      notify.error(getApiErrorMessage(error, 'department.saveError', t));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteDepartment = async (department) => {
    if (department.employeeCount > 0) {
      notify.warning(t('department.cannotDeleteWithEmployees', { count: department.employeeCount }));
      return;
    }

    try {
      await departmentService.delete(department.id);
      notify.success(t('department.deleteSuccess'));
      // Clear an active filter that pointed at the now-deleted department so the
      // grid never strands on an empty filtered view.
      setSelectedDepartmentId((current) => (current === department.id ? null : current));
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      notify.error(getApiErrorMessage(error, 'department.deleteError', t));
    }
  };

  const DepartmentCard = ({ department }) => (
      <Card
        hoverable
        className="department-card"
        actions={canManageDepartments() ? [
          <EditOutlined key="edit" data-testid="department-actions-edit" onClick={() => handleEditDepartment(department)} />,
          <Popconfirm
            title={t('department.deleteTitle')}
            description={t('department.deleteConfirm', { name: department.name })}
            onConfirm={() => handleDeleteDepartment(department)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            disabled={department.employeeCount > 0}
          >
            <DeleteOutlined
              data-testid="department-actions-delete"
              key="delete"
              style={{ color: department.employeeCount > 0 ? token.colorTextDisabled : undefined }}
            />
          </Popconfirm>
        ] : undefined}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleDepartmentCardClick(department)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleDepartmentCardClick(department);
            }
          }}
          style={{ cursor: 'pointer' }}
          title={canAccessEmployees() ? t('department.viewEmployeesHint') : t('department.viewMembersHint')}
          data-testid="department-card-navigate"
        >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space align="center">
            <div
              style={{
                width: 32,
                height: 32,
                backgroundColor: department.colorHex,
                borderRadius: '50%',
                border: `2px solid ${token.colorBorderSecondary}`,
                transition: 'transform 0.2s ease',
              }}
            />
            <Title level={4} style={{ margin: 0 }}>
              {department.name}
            </Title>
          </Space>

          <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
            {department.description || t('department.noDescription')}
          </Paragraph>

          <Space wrap>
            <Badge count={department.employeeCount || 0} showZero color={token.colorPrimary}>
              <Tag icon={<TeamOutlined />}>{t('department.employees')}</Tag>
            </Badge>
            <Tag color={department.colorHex}>{department.colorHex}</Tag>
          </Space>
        </Space>
        </div>
      </Card>
  );

  if (loading) {
    return (
      <div>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={2}>{t('department.title')}</Title>
          </div>
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4].map((key) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={key}>
                <Card>
                  <Skeleton active />
                </Card>
              </Col>
            ))}
          </Row>
        </Space>
      </div>
    );
  }

  const visibleDepartments =
    selectedDepartmentId == null
      ? departments
      : departments.filter((department) => department.id === selectedDepartmentId);

  return (
    <div className="department-list">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Title level={2} style={{ margin: 0 }}>{t('department.title')}</Title>
          <Space wrap>
            {departments.length > 0 && (
              <Select
                placeholder={t('department.filterPlaceholder')}
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 240 }}
                size="large"
                value={selectedDepartmentId}
                onChange={(value) => setSelectedDepartmentId(value ?? null)}
                data-testid="department-filter"
                options={departments.map((department) => ({
                  value: department.id,
                  label: department.name,
                  display: (
                    <Space>
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: department.colorHex,
                          borderRadius: '50%',
                          display: 'inline-block',
                        }}
                      />
                      {department.name}
                    </Space>
                  ),
                }))}
                optionRender={(option) => option.data.display}
              />
            )}
            {canManageDepartments() && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDepartment}
                size="large"
                data-testid="add-department-button"
              >
                {t('department.addDepartment')}
              </Button>
            )}
          </Space>
        </div>

        {departments.length === 0 ? (
          <Card>
            <Empty
              description={
                canManageDepartments()
                  ? t('department.emptyAdmin')
                  : t('department.emptyOther')
              }
            />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {visibleDepartments.map((department) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={department.id}>
                <DepartmentCard department={department} />
              </Col>
            ))}
          </Row>
        )}
      </Space>

      <DepartmentForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        department={editingDepartment}
        loading={formLoading}
      />

      <DepartmentMembersModal
        open={membersModalOpen}
        departmentId={selectedDepartment?.id}
        departmentName={selectedDepartment?.name}
        onClose={() => setMembersModalOpen(false)}
      />
    </div>
  );
};

export default DepartmentList;
